import { Lock, ShieldCheck } from 'lucide-react';
import { useId, useState } from 'react';
import type { FormEvent } from 'react';
import { ApiError, api } from '../lib/api';
import { formatPence } from '../lib/format';
import type { ProductCode, UnlockOption } from '../types/api';
import Notice from './Notice';
import Spinner from './Spinner';
import { tx } from '../i18n';

interface UnlockPanelProps {
  options: UnlockOption[];
  propertyAddressId: string | null;
  addressLine: string;
  bundleSlotsLeft?: number;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Buying the full report.
 *
 * <p>The bundle comparison is explicit, as section 19 asks: the per-home price sits next to the
 * headline price so "this home and four more for £4" is visible rather than implied. Nothing about
 * price is decided here; the options come from the server.
 */
export default function UnlockPanel({
  options,
  propertyAddressId,
  addressLine,
  bundleSlotsLeft = 0,
}: UnlockPanelProps) {
  const emailId = useId();
  const [selected, setSelected] = useState<ProductCode>('SINGLE_DETAIL');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // No options means nothing is for sale (payments switched off), so there is nothing to show.
  if (options.length === 0) {
    return null;
  }

  const single = options.find((option) => option.productCode === 'SINGLE_DETAIL');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = email.trim();
    if (!EMAIL_PATTERN.test(trimmed)) {
      setError(tx('Enter an email address we can send the report to.', 'Raporu gönderebileceğimiz bir e-posta adresi girin.',
    'Podaj adres e-mail, na który możemy wysłać raport.',
    'Indique um endereço de e-mail para onde possamos enviar o relatório.',
    'Indica un correo electrónico al que podamos enviar el informe.'));
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const session = await api.createCheckout({
        productCode: selected,
        propertyAddressId,
        email: trimmed,
      });
      window.location.assign(session.checkoutUrl);
    } catch (cause) {
      setError(
        cause instanceof ApiError ? cause.message : tx('We could not start checkout. Please try again.', 'Ödeme başlatılamadı. Lütfen tekrar deneyin.',
    'Nie udało się rozpocząć płatności. Spróbuj ponownie.',
    'Não foi possível iniciar o pagamento. Tente novamente.',
    'No se ha podido iniciar el pago. Inténtalo de nuevo.'),
      );
      setSubmitting(false);
    }
  };

  if (bundleSlotsLeft > 0) {
    return (
      <section className="unlock stack stack--tight" data-tour="unlock">
        <div className="row" style={{ gap: 10 }}>
          <ShieldCheck size={20} aria-hidden="true" style={{ color: '#1a51dd' }} />
          <h2 className="card__title">
            {tx(`You have ${bundleSlotsLeft} home(s) left on your bundle`, `Paketinizde ${bundleSlotsLeft} konut hakkı kaldı`,
    `W pakiecie pozostało ci ${bundleSlotsLeft} mieszkań`,
    `Ainda tem ${bundleSlotsLeft} casa(s) no seu pacote`,
    `Te quedan ${bundleSlotsLeft} vivienda(s) en tu pack`)}
          </h2>
        </div>
        <p className="small">
          {tx(
            `Opening the full report for ${addressLine} will use one of them. Homes you have already unlocked stay open and do not use another.`,
            `${addressLine} için tam raporu açmak bir hak kullanır. Kilidini açtığınız konutlar açık kalır ve yeni hak kullanmaz.`,
    `Otwarcie pełnego raportu dla ${addressLine} zużyje jedno z nich. Mieszkania już odblokowane pozostają otwarte i nie zużywają kolejnego.`,
    `Abrir o relatório completo de ${addressLine} vai usar uma delas. As casas que já desbloqueou continuam abertas e não gastam outra.`,
    `Abrir el informe completo de ${addressLine} usará una de ellas. Las viviendas ya desbloqueadas siguen abiertas y no gastan otra.`,
          )}
        </p>
      </section>
    );
  }

  return (
    <section className="unlock stack" data-tour="unlock">
      <div className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
        <Lock size={20} aria-hidden="true" style={{ color: '#1a51dd', marginTop: 2 }} />
        <div>
          <h2 className="card__title">{tx('Unlock the full report', 'Tam raporun kilidini açın',
    'Odblokuj pełny raport',
    'Desbloquear o relatório completo',
    'Desbloquear el informe completo')}</h2>
          <p className="small" style={{ marginTop: 4 }}>
            {tx(
              'Every category scored and ranked against its region, with the evidence behind each number, what to ask the landlord, and a one page PDF you can take to the viewing.',
              'Her kategori puanlanır ve kendi bölgesiyle kıyaslanır; her rakamın kanıtı, ev sahibine sorulacaklar ve ev gezisine götürebileceğiniz tek sayfalık PDF.',
    'Każda kategoria oceniona i uszeregowana na tle swojego regionu, z dowodami stojącymi za każdą liczbą, pytaniami do właściciela i jednostronicowym PDF-em, który zabierzesz na oglądanie.',
    'Cada categoria pontuada e comparada com a sua região, com as provas por detrás de cada número, o que perguntar ao senhorio e um PDF de uma página para levar à visita.',
    'Cada categoría puntuada y comparada con su región, con las pruebas detrás de cada cifra, qué preguntar al propietario y un PDF de una página para llevarte a la visita.',
            )}
          </p>
        </div>
      </div>

      <div className="grid grid--3" role="radiogroup" aria-label={tx('Choose what to unlock', 'Neyin kilidini açacağınızı seçin',
    'Wybierz, co odblokować',
    'Escolha o que desbloquear',
    'Elige qué desbloquear')}>
        {options.map((option) => {
          const isSelected = option.productCode === selected;
          const saving =
            single && option.perPropertyPence < single.amountPence
              ? Math.round(100 - (option.perPropertyPence / single.amountPence) * 100)
              : 0;

          return (
            <button
              key={option.productCode}
              type="button"
              role="radio"
              aria-checked={isSelected}
              className={isSelected ? 'plan plan--selected' : 'plan'}
              onClick={() => setSelected(option.productCode)}
            >
              <span className="plan__label">{option.label}</span>
              <span className="plan__price">{formatPence(option.amountPence)}</span>
              <span className="plan__per">
                {option.propertyLimit > 1
                  ? tx(
                      `${formatPence(option.perPropertyPence)} per home · ${option.validityDays} days`,
                      `konut başına ${formatPence(option.perPropertyPence)} · ${option.validityDays} gün`,
    `${formatPence(option.perPropertyPence)} za mieszkanie · ${option.validityDays} dni`,
    `${formatPence(option.perPropertyPence)} por casa · ${option.validityDays} dias`,
    `${formatPence(option.perPropertyPence)} por vivienda · ${option.validityDays} días`,
                    )
                  : tx('For this home', 'Bu konut için',
    'Dla tego mieszkania',
    'Para esta casa',
    'Para esta vivienda')}
              </span>
              {saving > 0 ? <span className="plan__save">{tx(`Save ${saving}%`, `%${saving} tasarruf`,
    `Oszczędzasz ${saving}%`,
    `Poupa ${saving}%`,
    `Ahorras un ${saving}%`)}</span> : null}
            </button>
          );
        })}
      </div>

      <form onSubmit={handleSubmit} noValidate className="field">
        <label htmlFor={emailId} className="label">
          {tx('Where should we send it?', 'Nereye gönderelim?',
    'Gdzie mamy go wysłać?',
    'Para onde o devemos enviar?',
    '¿Adónde te lo enviamos?')}
        </label>
        <div className="input-row">
          <input
            id={emailId}
            type="email"
            name="email"
            autoComplete="email"
            placeholder="you@example.co.uk"
            className="input"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              if (error) {
                setError(null);
              }
            }}
          />
          <button type="submit" className="button" disabled={submitting}>
            {submitting ? (
              <Spinner label={tx('Opening checkout', 'Ödeme açılıyor',
    'Otwieranie płatności',
    'A abrir o pagamento',
    'Abriendo el pago')} size={16} />
            ) : (
              tx('Continue to payment', 'Ödemeye geç',
    'Przejdź do płatności',
    'Continuar para o pagamento',
    'Continuar al pago')
            )}
          </button>
        </div>
      </form>

      {error ? (
        <Notice tone="error" title={tx('Checkout could not start', 'Ödeme başlatılamadı',
    'Nie udało się rozpocząć płatności',
    'Não foi possível iniciar o pagamento',
    'No se ha podido iniciar el pago')}>
          {error}
        </Notice>
      ) : null}

      <p className="tiny muted row" style={{ gap: 6 }}>
        <ShieldCheck size={13} aria-hidden="true" />
        {tx(
          'Payment is handled by Stripe. We never see your card details, and no account is needed.',
          'Ödeme Stripe tarafından alınır. Kart bilgilerinizi hiç görmeyiz ve hesap gerekmez.',
    'Płatność obsługuje Stripe. Nigdy nie widzimy danych twojej karty i nie trzeba zakładać konta.',
    'O pagamento é tratado pela Stripe. Nunca vemos os dados do seu cartão e não é preciso criar conta.',
    'El pago lo gestiona Stripe. Nunca vemos los datos de tu tarjeta y no hace falta crear cuenta.',
        )}
      </p>
    </section>
  );
}
